import type { Directive, DirectiveBinding } from 'vue';

// Reemplazo del title nativo con el tooltip del Poly design system: fondo oscuro, esquinas redondeadas, sin flecha.
// Hay un único elemento de tooltip compartido en el body, que se posiciona respecto del elemento con la directiva.
//
// Uso: v-tooltip="$t('Texto')"
//      v-tooltip="{ content: $t('Texto'), placement: 'bottom', maxWidth: 240 }"
// Un content vacío desactiva el tooltip.

type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';
type TooltipOptions = { content: string, placement?: TooltipPlacement, maxWidth?: number };
type TooltipValue = string | TooltipOptions | null | undefined;

const GAP = 6;
const VIEWPORT_MARGIN = 8;

// opciones vigentes de cada elemento con la directiva (se actualizan en updated)
const optionsByElement = new WeakMap<HTMLElement, TooltipOptions>();

// listeners agregados por la directiva, para poder removerlos en unmounted
const listenersByElement = new WeakMap<HTMLElement, { show: () => void, leave: (event: MouseEvent) => void }>();

let tooltipEl: HTMLDivElement | null = null;
let currentTarget: HTMLElement | null = null;

// Normaliza el valor de la directiva.
// in: value (string | { content, placement, maxWidth } | vacío)
// out: { content, placement, maxWidth }, content '' si no hay texto
function toOptions(value: TooltipValue): TooltipOptions {
  if (!value) return { content: '' };
  return typeof value === 'string' ? { content: value } : value;
}

// Devuelve el elemento compartido del tooltip, creándolo en el body la primera vez.
// out: div del tooltip
// efecto: agrega el div al body
function getTooltipEl(): HTMLDivElement {
  if (tooltipEl) return tooltipEl;
  tooltipEl = document.createElement('div');
  tooltipEl.setAttribute('role', 'tooltip');
  Object.assign(tooltipEl.style, {
    position: 'fixed',
    zIndex: '10000',
    display: 'none',
    padding: '5px 8px',
    background: '#333333',
    color: '#FFFFFF',
    borderRadius: '4px',
    fontFamily: 'pmxDefault',
    fontSize: '11.5px',
    lineHeight: '1.4',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.06)',
    pointerEvents: 'none',
    whiteSpace: 'pre-line', // respeta los saltos de línea del texto, como el title nativo
    overflowWrap: 'break-word',
  });
  document.body.appendChild(tooltipEl);
  return tooltipEl;
}

// Calcula la posición del tooltip junto al elemento; si no entra del lado pedido, usa el opuesto, y lo mantiene dentro
// del viewport.
// in: target (rect del elemento), tooltip ({ width, height }), placement
// out: { top, left } en px, relativos al viewport
function computePosition(target: DOMRect, tooltip: { width: number, height: number }, placement: TooltipPlacement) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let side = placement;
  if (side === 'top' && target.top - GAP - tooltip.height < 0) side = 'bottom';
  else if (side === 'bottom' && target.bottom + GAP + tooltip.height > vh) side = 'top';
  else if (side === 'left' && target.left - GAP - tooltip.width < 0) side = 'right';
  else if (side === 'right' && target.right + GAP + tooltip.width > vw) side = 'left';

  let top: number;
  let left: number;
  if (side === 'top' || side === 'bottom') {
    top = side === 'top' ? target.top - GAP - tooltip.height : target.bottom + GAP;
    left = target.left + target.width / 2 - tooltip.width / 2;
  } else {
    top = target.top + target.height / 2 - tooltip.height / 2;
    left = side === 'left' ? target.left - GAP - tooltip.width : target.right + GAP;
  }

  const clamp = (value: number, max: number) => Math.min(Math.max(value, VIEWPORT_MARGIN), max - VIEWPORT_MARGIN);
  return { top: clamp(top, vh - tooltip.height), left: clamp(left, vw - tooltip.width) };
}

// Muestra el tooltip del elemento con sus opciones vigentes, o lo oculta si no tiene texto.
// in: el (elemento con la directiva)
// efecto: actualiza el texto, tamaño y posición del tooltip compartido
function showFor(el: HTMLElement) {
  const options = optionsByElement.get(el);
  if (!options?.content) return hide();
  const tooltip = getTooltipEl();
  currentTarget = el;
  tooltip.textContent = options.content;
  tooltip.style.maxWidth = (options.maxWidth || 200) + 'px';
  tooltip.style.display = 'block';
  const { top, left } = computePosition(el.getBoundingClientRect(), tooltip.getBoundingClientRect(), options.placement || 'top');
  tooltip.style.top = top + 'px';
  tooltip.style.left = left + 'px';
}

// efecto: oculta el tooltip compartido
function hide() {
  currentTarget = null;
  if (tooltipEl) tooltipEl.style.display = 'none';
}

// Al salir de un elemento hacia un ancestro que también tiene tooltip (p. ej. un ícono dentro de una línea con su
// propio tooltip), se vuelve a mostrar el del ancestro, como hace el title nativo; si no, se oculta.
// in: event (mouseleave; relatedTarget es el elemento al que entra el mouse)
function onLeave(event: MouseEvent) {
  let node = event.relatedTarget as HTMLElement | null;
  while (node && !optionsByElement.has(node)) node = node.parentElement;
  if (node) showFor(node);
  else hide();
}

// Al scrollear el tooltip quedaría desfasado del elemento: se oculta
window.addEventListener('scroll', () => { if (currentTarget) hide(); }, true);

function mounted(el: HTMLElement, binding: DirectiveBinding<TooltipValue>) {
  optionsByElement.set(el, toOptions(binding.value));
  const listeners = { show: () => showFor(el), leave: onLeave };
  el.addEventListener('mouseenter', listeners.show);
  el.addEventListener('mouseleave', listeners.leave);
  // Como el title nativo, se oculta al apretar: si el click abre un modal, el tooltip no queda flotando encima
  el.addEventListener('mousedown', hide);
  listenersByElement.set(el, listeners);
}

// Si el texto cambia mientras el tooltip está abierto, se refresca
function updated(el: HTMLElement, binding: DirectiveBinding<TooltipValue>) {
  optionsByElement.set(el, toOptions(binding.value));
  if (currentTarget === el) showFor(el);
}

function unmounted(el: HTMLElement) {
  const listeners = listenersByElement.get(el);
  if (listeners) {
    el.removeEventListener('mouseenter', listeners.show);
    el.removeEventListener('mouseleave', listeners.leave);
    el.removeEventListener('mousedown', hide);
    listenersByElement.delete(el);
  }
  optionsByElement.delete(el);
  if (currentTarget === el) hide();
}

export const tooltipDirective: Directive<HTMLElement, TooltipValue> = { mounted, updated, unmounted };
